<template>
  <div
    v-if="native"
    class="language-select language-select--native"
  >
    <Icon
      :key="currentLocale"
      :name="getIconName(currentLocale)"
      size="30"
      class="language-select__current-language"
    />
    <ClientOnly>
      <AppSelect
        id="language"
        name="language"
        :aria-label="$t('language_switch.aria_label')"
        :options="appSelectOptions"
        :model-value="currentLocale"
        class="language-select__app-select"
        @update:model-value="selectLocale"
      />
      <template #fallback>
        <AppSelect
          id="language"
          name="language"
          :aria-label="$t('language_switch.aria_label')"
          class="language-select__app-select"
        />
      </template>
    </ClientOnly>
  </div>
  <div
    v-else
    v-on-click-outside="closeOtherLanguages"
    class="language-select"
  >
    <div class="language-select__other-option">
      <Icon
        :key="currentLocale"
        :name="getIconName(currentLocale)"
        size="30"
        class="language-select__other-option-icon"
        @click="toggleOtherLanguages"
      />
    </div>
    <transition name="fade">
      <div
        v-if="showOtherLanguageOptions"
        class="language-select__other-options"
      >
        <div
          v-for="locale in filteredLocales"
          :key="locale.code"
          class="language-select__other-option"
        >
          <Icon
            :to="switchLocalePath(locale.code)"
            :name="getIconName(locale.code)"
            size="30"
            class="language-select__other-option-icon"
            @click="selectLocale(locale.code)"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
  import { vOnClickOutside } from '@vueuse/components'
  import { storeToRefs } from 'pinia'
  import { useUserStore } from '~/stores/user'

  const userStore = useUserStore()
  const userService = useUserService()
  const { isAuthenticated } = storeToRefs(userStore)
  const props = defineProps({
    native: {
      type: Boolean,
      default: false,
    },
  })

  const { t: $t, locale: currentLocale, locales, setLocale } = useI18n()
  const switchLocalePath = useSwitchLocalePath()

  const router = useRouter()
  const filteredLocales = computed(() => locales.value.filter((locale) => locale.code !== currentLocale.value))
  const appSelectOptions = computed(() =>
    locales.value.map((locale) => ({
      key: locale.name,
      value: locale.code,
    })),
  )

  const loaded = ref(false)
  const showOtherLanguageOptions = ref(false)

  onMounted(() => {
    loaded.value = true
  })

  function getIconName(locale) {
    return `custom:flag-${locale}`
  }

  function closeOtherLanguages() {
    showOtherLanguageOptions.value = false
  }

  function toggleOtherLanguages() {
    showOtherLanguageOptions.value = !showOtherLanguageOptions.value
  }

  async function selectLocale(localeCode) {
    if (isAuthenticated.value) {
      userService.updateUserlanguage(localeCode)
    }

    if (props.native) {
      setLocale(localeCode)
      router.push(switchLocalePath(localeCode))

      return
    }

    closeOtherLanguages()
    setLocale(localeCode)

    setTimeout(() => {
      router.push(switchLocalePath(localeCode))
    }, 300) // Wait for the fade out transition
  }
</script>

<style lang="scss">
  .language-select {
    position: relative;
    color: var(--color-primary);
  }

  .language-select--native {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .language-select__current-language {
    flex-shrink: 0;
    border-radius: 50%;
    overflow: hidden;
  }

  .language-select__app-select {
    flex-grow: 1;
    background: transparent;
    color: white;
    border: none;
  }

  .language-select__app-select .app-select__select {
    background: var(--color-primary);
    border: 2px solid var(--color-primary);
  }

  .language-select__app-select .app-select__select:focus-visible {
    outline: none;
  }

  .language-select__other-options {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 1;
  }

  .language-select__other-option {
    border-radius: 50%;
    cursor: pointer;
    position: relative;

    &::after {
      content: '';
      height: 94%;
      width: 94%;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%) scale(1);
      transition: 0.2s transform ease-out;
      z-index: -1;
      background: var(--color-secondary);
      border-radius: 50%;
    }

    &:hover::after {
      transform: translate(-50%, -50%) scale(1.2);
    }
  }
</style>
